import { CSSelectOption, CSSelectOptions } from "types";
import {
  FieldValues,
  Path,
  UseFormReturn,
  ValidationRule,
  ValidationValue,
  ValidationValueMessage,
  get,
} from "react-hook-form";

import { parseNumberOrNull } from "utils/numbers";

type GetValidationPropsOptions = {
  hideValid?: boolean;
};

export const isValidationValueMessage = <T extends ValidationValue>(
  val: ValidationRule<T> | undefined,
): val is ValidationValueMessage<T> => typeof val === "object";

/**
 * Extracts the "value" of a validation rule used by hook form.
 * Hook form allows for 2 shapes:
 *
 * 1. directly as a string
 * 2. within a `{ value: string }` record
 *
 * This function handles both and returns the value if it is present.
 */
export const getValidationValue = <T extends ValidationValue>(val: ValidationRule<T> | undefined): T | undefined =>
  isValidationValueMessage(val) ? val.value : val;

/**
 * If `val` is truthy then it is wrapped in an array; otherwise returns `undefined`.
 *
 * This is a convenience for setting values to our CSSelectSearch which always deals with an array.
 */
export const wrapSelectSearchValue = (val: string | number | null | undefined): (string | number)[] | undefined =>
  val ? [val] : undefined;

/**
 * Unwraps a single value from a `CSSelectOption[]`.
 * This will result in a (string | number) - we have other wrappers to get just `string` or just `number` if needed.
 */
export const unwrapSelectSearchValue = (val: CSSelectOption[]): string | number | null => val?.[0].value ?? null;

/**
 * Unwraps a single value from a `CSSelectOption[]` to a string; otherwise null.
 */
export const unwrapSelectSearchAsString = (val: CSSelectOption[]): string | null =>
  unwrapSelectSearchValue(val)?.toString() ?? null;

/**
 * Unwraps a single value from a `CSSelectOption[]` to a number; otherwise null.
 */
export const unwrapSelectSearchAsNumber = (val: CSSelectOption[]): number | null =>
  parseNumberOrNull(unwrapSelectSearchValue(val));

/**
 * Returns the `isInvalid` and `isValid` props based on current validation errors.
 * Normally you will spread the result of this function onto your component's props.
 *
 * TODO: Usually we use `getFieldValidationAttributes` from chops to handle isInvalid/isValid.
 * We have some fields that have nested names though: `customFixed.minDate`, `customFixed.maxDate`, etc.
 * The chops method does NOT handle the dot syntax so validation messages do not show up on those cases.
 * We should update Chops, but would need a way to handle the dot syntax - maybe a peer dep on react-hook-form?
 * Also noting that we use a similar approach in DashletDateFilter for the relative time option.
 */
export const getValidationProps = <TFieldValues extends FieldValues>(
  formApi: UseFormReturn<TFieldValues>,
  path: Path<TFieldValues>,
  options: GetValidationPropsOptions = {},
) => {
  const hasError = !!get(formApi.formState.errors, path);
  return {
    isInvalid: hasError,
    isValid: options.hideValid ? false : formApi.formState.isSubmitted && !hasError,
  };
};

/**
 * Recursively searches `options` to find if there is an item with a matching value.
 * Returns the item if found, otherwise `null`.
 */
export const findSelectOption = (val: string | number, options: CSSelectOptions = []): CSSelectOption | null => {
  const [item, ...items] = options;
  if (!item) {
    return null;
  }

  if ("value" in item) {
    return item.value === val ? item : findSelectOption(val, items);
  }

  // search through children options
  const child = findSelectOption(val, item.options);

  // if it is NOT in the children continue searching siblings
  return child ? child : findSelectOption(val, items);
};
